import { Typography, Tooltip } from 'antd';
import React from 'react';
import { ConditionalWrapper } from '@shared/ui/conditional-wrapper';

export type TypographyVariant =
  | '7xl'
  | '6xl'
  | '5xl'
  | '4xl'
  | '3xl'
  | '2xl'
  | 'xl'
  | 'lg'
  | 'md'
  | 'sm'
  | 'xs'
  | 'xxs';

type Props = {
  variant?: TypographyVariant;
  fontWeight?: React.CSSProperties['fontWeight'];
  children: React.ReactNode;
  style?: React.CSSProperties;
  hasTooltip?: boolean;
  maxLength?: number;
} & Omit<React.ComponentProps<typeof Typography.Text>, 'children'>;

const variantStyles: Record<TypographyVariant, React.CSSProperties> = {
  '7xl': { fontSize: '60px', lineHeight: '100%' },
  '6xl': { fontSize: '48px', lineHeight: '100%' },
  '5xl': { fontSize: '42px', lineHeight: '100%' },
  '4xl': { fontSize: '36px', lineHeight: '100%' },
  '3xl': { fontSize: '28px', lineHeight: '36px' },
  '2xl': { fontSize: '24px', lineHeight: '32px' },
  xl: { fontSize: '20px', lineHeight: '28px' },
  lg: { fontSize: '18px', lineHeight: '28px' },
  md: { fontSize: '16px', lineHeight: '24px' },
  sm: { fontSize: '14px', lineHeight: '20px' },
  xs: { fontSize: '12px', lineHeight: '16px' },
  xxs: { fontSize: '10px', lineHeight: '16px' },
};

export const Text = ({
  variant = 'md',
  fontWeight = 400,
  style,
  children,
  hasTooltip = false,
  maxLength = 80,
  ...props
}: Props) => {
  const isText = typeof children === 'string';
  const shouldTruncate = hasTooltip && isText && children.length > maxLength;

  const displayText = shouldTruncate ? `${children.slice(0, maxLength)}...` : children;

  const textNode = (
    <Typography.Text
      style={{
        ...variantStyles[variant],
        fontWeight,
        ...style,
      }}
      {...props}
    >
      {displayText}
    </Typography.Text>
  );

  return (
    <ConditionalWrapper
      condition={shouldTruncate}
      wrapper={(node) => (
        <Tooltip title={children} placement="topLeft">
          {node}
        </Tooltip>
      )}
    >
      {textNode}
    </ConditionalWrapper>
  );
};
